import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCoreT } from '../context/CoreLangContext';
import PhoneOtpForm, { buildOtpLabels } from '../components/PhoneOtpForm';

export default function AdminLoginPage({ restaurant }) {
  const navigate = useNavigate();
  const T = useCoreT();
  const { loginAdmin, sendAdminOtp, verifyAdminOtp } = useAuth();
  const [method,   setMethod]   = useState('password'); // 'password' | 'phone'
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const handleLogin = async () => {
    if (!email || !password) { setError(T.error); return; }
    setLoading(true); setError('');
    const res = await loginAdmin(restaurant.slug, email, password);
    setLoading(false);
    if (res.success) navigate('/admin');
    else setError(res.error);
  };

  return (
    <div className="al-page" dir={T.dir} style={{ '--primary': restaurant.primaryColor }}>
      <div className="al-card">
        <div className="al-logo">🍽️</div>
        <h1 className="al-restaurant">{restaurant.name}</h1>
        <p className="al-sub">{T.adminLogin}</p>

        {error && <div className="al-error">{error}</div>}

        <div className="al-method">
          <button className={`al-mbtn ${method==='phone'?'active':''}`} onClick={() => { setMethod('phone'); setError(''); }}>📱 {T.phone || 'Phone'}</button>
          <button className={`al-mbtn ${method==='password'?'active':''}`} onClick={() => { setMethod('password'); setError(''); }}>🔑 {T.identifier || 'Username / Email'}</button>
        </div>

        {method === 'phone' ? (
          <PhoneOtpForm
            accent={restaurant.primaryColor}
            lang={T.lang}
            labels={buildOtpLabels(T)}
            onSend={(phone) => sendAdminOtp(restaurant.slug, phone)}
            onVerify={(phone, code) => verifyAdminOtp(restaurant.slug, phone, code).then(r => { if (r.success) navigate('/admin'); return r; })}
          />
        ) : (
          <>
            <div className="al-fields">
              <div className="al-field">
                <label>{T.identifier || 'Username or Email'}</label>
                <input type="text" className="al-input" value={email}
                  autoCapitalize="none" autoCorrect="off" spellCheck={false}
                  onChange={e => setEmail(e.target.value)} placeholder={T.identifier || 'Username or Email'}
                  onKeyDown={e => e.key === 'Enter' && handleLogin()} autoFocus />
              </div>
              <div className="al-field">
                <label>{T.password}</label>
                <input type="password" className="al-input" value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="••••••••"
                  onKeyDown={e => e.key === 'Enter' && handleLogin()} />
              </div>
            </div>

            <button className="al-btn" onClick={handleLogin} disabled={loading}
              style={{ background: restaurant.primaryColor }}>
              {loading ? T.signingIn : T.signIn}
            </button>
          </>
        )}

        <div className="al-links">
          <button className="al-link" onClick={() => navigate('/')}>{T.backToSite}</button>
          <button className="al-link" onClick={() => navigate('/business/login')}>{T.businessOwnerLogin}</button>
        </div>
      </div>

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0a0a0a; }
        .al-page { min-height: 100vh; background: #0a0a0a; display: flex; align-items: center; justify-content: center; font-family: 'DM Sans', sans-serif; padding: 2rem; }
        .al-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); border-radius: 24px; padding: 3rem; max-width: 420px; width: 100%; text-align: center; }
        .al-logo { font-size: 3rem; margin-bottom: 1rem; }
        .al-restaurant { font-family: 'Playfair Display', serif; font-size: 1.8rem; color: #fff; margin-bottom: 0.5rem; }
        .al-sub { color: rgba(255,255,255,0.4); font-size: 0.9rem; margin-bottom: 2rem; text-transform: uppercase; letter-spacing: 0.1em; }
        .al-error { background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3); border-radius: 10px; padding: 0.75rem 1rem; color: #fca5a5; font-size: 0.85rem; margin-bottom: 1.5rem; }
        .al-method { display: flex; gap: 0.5rem; margin-bottom: 1.5rem; }
        .al-mbtn { flex: 1; padding: 0.6rem; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; color: rgba(255,255,255,0.55); cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: 0.85rem; font-weight: 600; transition: all 0.2s; }
        .al-mbtn.active { background: var(--primary); border-color: var(--primary); color: #fff; }
        .al-fields { display: flex; flex-direction: column; gap: 1rem; margin-bottom: 1.5rem; text-align: left; }
        .al-field { display: flex; flex-direction: column; gap: 0.5rem; }
        .al-field label { font-size: 0.75rem; color: rgba(255,255,255,0.45); text-transform: uppercase; letter-spacing: 0.08em; }
        .al-input { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; padding: 0.85rem 1rem; color: #fff; font-family: 'DM Sans', sans-serif; font-size: 0.95rem; outline: none; transition: border-color 0.2s; }
        .al-input:focus { border-color: var(--primary); }
        .al-btn { width: 100%; padding: 1rem; border: none; border-radius: 12px; color: #fff; font-family: 'DM Sans', sans-serif; font-size: 1rem; font-weight: 700; cursor: pointer; transition: opacity 0.2s; margin-bottom: 1.5rem; }
        .al-btn:hover:not(:disabled) { opacity: 0.85; }
        .al-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .al-hint { font-size: 0.75rem; color: rgba(255,255,255,0.25); margin-bottom: 1.5rem; line-height: 1.6; }
        .al-hint code { color: rgba(255,255,255,0.45); background: rgba(255,255,255,0.06); padding: 0.1rem 0.4rem; border-radius: 4px; font-size: 0.7rem; }
        .al-links { display: flex; justify-content: space-between; }
        .al-link { background: none; border: none; color: rgba(255,255,255,0.35); cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: 0.8rem; transition: color 0.2s; }
        .al-link:hover { color: rgba(255,255,255,0.7); }
      `}</style>
    </div>
  );
}
